import {
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Accordion, { AccordionProps } from "@saleor/components/Accordion";
import Checkbox from "@saleor/components/Checkbox";
import Chip from "@saleor/components/Chip";
import Hr from "@saleor/components/Hr";
import { MultiAutocompleteChoiceType } from "@saleor/components/MultiAutocompleteSelectField";
import { ChangeEvent, FormChange } from "@saleor/hooks/useForm";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import { sectionNames } from "@saleor/intl";
import { ProductsUser } from "@saleor/products/customTypes/ProductsUser";
import { FetchMoreProps } from "@saleor/types";
import {
  ExportProductsInput,
  ProductFieldEnum,
  ProductTemplateEnum
} from "@saleor/types/globalTypes";
import { toggle } from "@saleor/utils/lists";
import React from "react";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";

import useProductExportFieldMessages from "./messages";

export const attributeNamePrefix = "attribute-";
export const warehouseNamePrefix = "warehouse-";
const maxChips = 4;

const inventoryFields = [
  // ProductFieldEnum.PRODUCT_WEIGHT,
  ProductFieldEnum.VARIANT_SKU
  // ProductFieldEnum.VARIANT_WEIGHT
];

const useStyles = makeStyles(
  theme => ({
    accordion: {
      marginBottom: theme.spacing(2)
    },
    checkbox: {
      position: "relative",
      right: -theme.spacing(1.5)
    },
    chip: {
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing()
    },
    dialogErrorLabel: {
      color: "red",
      marginBottom: theme.spacing(2)
    },
    dialogLabel: {
      marginBottom: theme.spacing(2)
    },
    hr: {
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(3)
    },
    hrWarehouses: {
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(1)
    },
    label: {
      "&&": {
        overflow: "visible"
      },
      "&:first-of-type": {
        paddingTop: 0
      },
      "&:not(:last-of-type)": {
        borderBottom: `1px solid ${theme.palette.divider}`
      },
      justifyContent: "space-between",
      margin: theme.spacing(0),
      padding: theme.spacing(1, 0),
      width: "100%"
    },
    loadMoreContainer: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(2)
    },
    moreLabel: {
      display: "inline-block",
      marginBottom: theme.spacing()
    },
    optionLabel: {
      marginLeft: 0
    },
    quickPeekContainer: {
      marginBottom: theme.spacing(-1)
    },
    warehousesLabel: {
      marginBottom: theme.spacing(2)
    }
  }),
  {
    name: "ProductExportDialogInfo"
  }
);

const Option: React.FC<{
  idIndex?: string;
  idOption?: string;
  checked: boolean;
  name: string;
  onChange: (event: ChangeEvent) => void;
}> = ({ checked, children, name, onChange, idIndex, idOption }) => {
  const classes = useStyles({});

  return (
    <FormControlLabel
      classes={{
        label: classes.optionLabel
      }}
      color="primary"
      control={
        <Checkbox
          className={classes.checkbox}
          id={idIndex ? `${idOption}CheckboxRow${idIndex}` : null}
          checked={checked}
          name={name}
          onChange={onChange}
        />
      }
      className={classes.label}
      label={children}
      id={idOption && idIndex ? `${idOption}Row${idIndex}` : null}
      labelPlacement="start"
    ></FormControlLabel>
  );
};

const FieldAccordion: React.FC<AccordionProps & {
  data: ExportProductsInput;
  fields?: ProductFieldEnum[];
  onChange: (event: ChangeEvent) => void;
  onToggleAll: (field: ProductFieldEnum[], setTo: boolean) => void;
}> = ({ data, fields, onChange, onToggleAll, ...props }) => {
  const classes = useStyles({});
  const getFieldLabel = useProductExportFieldMessages();

  const selectedAll = fields.every(field =>
    data.exportInfo.fields.includes(field)
  );

  const selectedFields = data.exportInfo.fields.filter(field =>
    fields.includes(field)
  );

  return (
    <Accordion
      quickPeek={
        selectedFields.length > 0 && (
          <div className={classes.quickPeekContainer}>
            {selectedFields.slice(0, maxChips).map(field => (
              <Chip
                className={classes.chip}
                label={getFieldLabel(field)}
                onClose={() =>
                  onChange({
                    target: {
                      name: field,
                      value: false
                    }
                  })
                }
                key={field}
              />
            ))}
            {selectedFields.length > maxChips && (
              <Typography className={classes.moreLabel} variant="caption">
                <FormattedMessage
                  defaultMessage="and {number} more"
                  description="there are more elements of list that are hidden"
                  values={{
                    number: selectedFields.length - maxChips
                  }}
                />
              </Typography>
            )}
          </div>
        )
      }
      {...props}
    >
      <Option
        checked={selectedAll}
        name="all"
        onChange={() => onToggleAll(fields, !selectedAll)}
      >
        <FormattedMessage
          defaultMessage="Select All"
          description="selectt all options"
        />
      </Option>
      {fields.map(field => (
        <Option
          checked={data.exportInfo.fields.includes(field)}
          name={field}
          onChange={onChange}
          key={field}
        >
          {getFieldLabel(field)}
        </Option>
      ))}
    </Accordion>
  );
};

export interface ProductExportDialogInfoProps extends FetchMoreProps {
  attributes: MultiAutocompleteChoiceType[];
  warehouses: MultiAutocompleteChoiceType[];
  malls: MultiAutocompleteChoiceType[];
  data: ExportProductsInput;
  hasMallsError: boolean;
  selectedAttributes: MultiAutocompleteChoiceType[];
  onAttrtibuteSelect: FormChange;
  onMallSelect: FormChange;
  onWarehouseSelect: FormChange;
  onChange: FormChange;
  onFetch: (query: string) => void;
  onSelectAllWarehouses: FormChange;
  productsUser: ProductsUser;
}

const ProductExportDialogInfo: React.FC<ProductExportDialogInfoProps> = ({
  attributes,
  data,
  hasMore,
  hasMallsError,
  malls,
  selectedAttributes,
  loading,
  warehouses,
  onAttrtibuteSelect,
  onMallSelect,
  onWarehouseSelect,
  onChange,
  onFetch,
  onFetchMore,
  onSelectAllWarehouses,
  productsUser
}) => {
  const classes = useStyles({});
  const intl = useIntl();
  const [query, onQueryChange] = useSearchQuery(onFetch);
  const getFieldLabel = useProductExportFieldMessages();

  const handleTemplateChange = (event: ChangeEvent) =>
    onChange({
      target: {
        name: "exportInfo",
        value: {
          ...data.exportInfo,
          template: event.target.value
        }
      }
    });
  const handleFieldChange = (event: ChangeEvent) =>
    onChange({
      target: {
        name: "exportInfo",
        value: {
          ...data.exportInfo,
          fields: toggle(
            event.target.name,
            data.exportInfo.fields,
            (a, b) => a === b
          )
        }
      }
    });

  const handleToggleAllFields = (fields: ProductFieldEnum[], setTo: boolean) =>
    onChange({
      target: {
        name: "exportInfo",
        value: {
          ...data.exportInfo,
          fields: setTo
            ? [
                ...data.exportInfo.fields,
                ...fields.filter(
                  field => !data.exportInfo.fields.includes(field)
                )
              ]
            : data.exportInfo.fields.filter(field => !fields.includes(field))
        }
      }
    });

  const selectedInventoryFields = data.exportInfo.fields.filter(field =>
    inventoryFields.includes(field)
  );
  const selectedAllInventoryFields =
    selectedInventoryFields.length === inventoryFields.length;

  const sellerMasterProductTemplates = [
    ProductTemplateEnum.IMAGES_AND_DESCRIPTIONS,
    ProductTemplateEnum.LIST,
    ProductTemplateEnum.PRICES,
    ProductTemplateEnum.INVENTORY_AND_STATUS
  ];

  const storeAdminProductTemplate = [ProductTemplateEnum.INVENTORY_AND_STATUS];

  const financialTemplate = [ProductTemplateEnum.RETAIL_AND_SELLING_PRICE];

  const productTemplates = productsUser.isStoreAdmin
    ? storeAdminProductTemplate
    : sellerMasterProductTemplates;

  return (
    <>
      <Typography className={classes.dialogLabel}>
        <FormattedMessage
          defaultMessage="Information exported:"
          description="select product informations to be exported"
        />
      </Typography>
      {/* {user.user.userType.code === SmopUserTypeCode.STORE_ADMIN && (
        <FieldAccordion
          className={classes.accordion}
          title={intl.formatMessage({
            defaultMessage: "Product Information",
            description: "informations about product prices etc, header"
          })}
          data={data}
          fields={[ProductFieldEnum.PRODUCT_STATUS]}
          onChange={handleFieldChange}
          onToggleAll={handleToggleAllFields}
          data-test="product info"
        />
      )} */}
      <Accordion
        className={classes.accordion}
        title={intl.formatMessage({
          defaultMessage: "Product Information",
          description: "informations about product, header"
        })}
        quickPeek={
          data.exportInfo.template &&
          data.exportInfo.template !==
            ProductTemplateEnum.RETAIL_AND_SELLING_PRICE &&
          data.exportInfo.template !==
            ProductTemplateEnum.SEO_INFORMATION_IMAGES && (
            <Chip
              className={classes.chip}
              label={data.exportInfo.template.replace(/_/g, " ")}
              key={data.exportInfo.template}
              onClose={() =>
                onChange({
                  target: {
                    name: "exportInfo",
                    value: {
                      ...data.exportInfo,
                      template: null
                    }
                  }
                })
              }
            />
          )
        }
        data-test="productInformation"
        idAccordion="productInformation"
      >
        <RadioGroup aria-label="template" onChange={handleTemplateChange}>
          {productTemplates.map((tpl, index) => (
            <FormControlLabel
              name={data.exportInfo.template}
              key={index}
              value={tpl}
              id={`productInformationRadioRow${index}`}
              control={<Radio checked={tpl === data.exportInfo.template} />}
              label={
                "Product " +
                tpl
                  .toLowerCase()
                  .charAt(0)
                  .toUpperCase() +
                tpl
                  .slice(1)
                  .replace(/_/g, " ")
                  .toLowerCase()
                  .replace("descriptions", "Descriptions")
                  .replace("status", "Status")
              }
            />
          ))}
        </RadioGroup>
        {productsUser.isSellerMaster &&
          data.exportInfo.template ===
            ProductTemplateEnum.INVENTORY_AND_STATUS && (
            <>
              <Hr className={classes.hr} />
              {hasMallsError && (
                <Typography className={classes.dialogErrorLabel}>
                  <FormattedMessage
                    defaultMessage="Please select a mall:"
                    description="select product informations to be exported"
                  />
                </Typography>
              )}
              {malls.map((mall, index) => (
                <Option
                  checked={data.filter.malls.includes(mall.value)}
                  name={attributeNamePrefix + mall.value}
                  onChange={onMallSelect}
                  key={mall.value}
                  idIndex={`${index}`}
                  idOption="productInventoryStatusMall"
                >
                  {mall.label}
                </Option>
              ))}
            </>
          )}
      </Accordion>
      {!productsUser.isSellerMaster && !productsUser.isStoreAdmin && (
        <Accordion
          className={classes.accordion}
          title={intl.formatMessage(sectionNames.attributes)}
          quickPeek={
            selectedAttributes.length > 0 && (
              <div className={classes.quickPeekContainer}>
                {selectedAttributes.slice(0, maxChips).map(attribute => (
                  <Chip
                    className={classes.chip}
                    label={attribute.label}
                    onClose={() =>
                      onAttrtibuteSelect({
                        target: {
                          name: attributeNamePrefix + attribute.value,
                          value: undefined
                        }
                      })
                    }
                    key={attribute.value}
                  />
                ))}
                {selectedAttributes.length > maxChips && (
                  <Typography className={classes.moreLabel} variant="caption">
                    <FormattedMessage
                      defaultMessage="and {number} more"
                      description="there are more elements of list that are hidden"
                      values={{
                        number: selectedAttributes.length - maxChips
                      }}
                    />
                  </Typography>
                )}
              </div>
            )
          }
          data-test="attributes"
        >
          <TextField
            name="query"
            value={query || ""}
            onChange={onQueryChange}
            label={intl.formatMessage({
              defaultMessage: "Search Atrtibuttes"
            })}
            placeholder={intl.formatMessage({
              defaultMessage: "Search by attribute name",
              description: "input helper text, search attributes"
            })}
            fullWidth
            InputProps={{
              autoComplete: "off",
              endAdornment: loading && <CircularProgress size={16} />
            }}
          />
          <Hr className={classes.hr} />
          {attributes.map(attribute => (
            <Option
              checked={data.exportInfo.attributes.includes(attribute.value)}
              name={attributeNamePrefix + attribute.value}
              onChange={onAttrtibuteSelect}
              key={attribute.value}
            >
              {attribute.label}
            </Option>
          ))}
          {(hasMore || loading) && (
            <div className={classes.loadMoreContainer}>
              {hasMore && !loading && (
                <Button color="primary" onClick={onFetchMore}>
                  <FormattedMessage
                    defaultMessage="Load More"
                    description="button"
                  />
                </Button>
              )}
              {loading && <CircularProgress size={32} />}
            </div>
          )}
        </Accordion>
      )}
      <Accordion
        className={classes.accordion}
        idAccordion="financialInformation"
        title={intl.formatMessage({
          defaultMessage: "Financial Information",
          description: "informations about product, header"
        })}
        quickPeek={
          data.exportInfo.template &&
          data.exportInfo.template ===
            ProductTemplateEnum.RETAIL_AND_SELLING_PRICE && (
            <Chip
              className={classes.chip}
              label={data.exportInfo.template.replace(/_/g, " ")}
              key={data.exportInfo.template}
              onClose={() =>
                onChange({
                  target: {
                    name: "exportInfo",
                    value: {
                      ...data.exportInfo,
                      template: null
                    }
                  }
                })
              }
            />
          )
        }
        data-test="productInformation"
      >
        <RadioGroup aria-label="template" onChange={handleTemplateChange}>
          {financialTemplate.map((tpl, index) => (
            <FormControlLabel
              id={`financialInformationRadioRow${index}`}
              name={data.exportInfo.template}
              key={index}
              value={tpl}
              control={<Radio checked={tpl === data.exportInfo.template} />}
              label={
                "Product " +
                tpl
                  .toLowerCase()
                  .charAt(0)
                  .toUpperCase() +
                tpl
                  .slice(1)
                  .replace(/_/g, " ")
                  .toLowerCase()
                  .replace("selling price", "Selling Price")
              }
            />
          ))}
        </RadioGroup>
        {productsUser.isSellerMaster &&
          data.exportInfo.template ===
            ProductTemplateEnum.RETAIL_AND_SELLING_PRICE && (
            <>
              <Hr className={classes.hr} />
              {hasMallsError && (
                <Typography className={classes.dialogErrorLabel}>
                  <FormattedMessage
                    defaultMessage="Please select a mall:"
                    description="select product informations to be exported"
                  />
                </Typography>
              )}
              {malls.map((mall, index) => (
                <Option
                  checked={data.filter.malls.includes(mall.value)}
                  name={attributeNamePrefix + mall.value}
                  onChange={onMallSelect}
                  key={mall.value}
                  idIndex={`${index}`}
                  idOption="productRetailAndSellingPriceMall"
                >
                  {mall.label}
                </Option>
              ))}
            </>
          )}
      </Accordion>
      {/* <FieldAccordion
        className={classes.accordion}
        title={intl.formatMessage({
          defaultMessage: "Financial Information",
          description: "informations about product prices etc, header"
        })}
        data={data}
        fields={[
          ProductFieldEnum.CHARGE_TAXES,
          ProductFieldEnum.COST_PRICE,
          ProductFieldEnum.VARIANT_PRICE,
          ProductFieldEnum.VISIBLE,
          ProductFieldEnum.AVAILABLE_FOR_PURCHASE
        ]}
        onChange={handleFieldChange}
        onToggleAll={handleToggleAllFields}
        data-test="financial"
      /> */}
      {!productsUser.isSellerMaster && !productsUser.isStoreAdmin && (
        <Accordion
          idAccordion="inventoryInformation"
          className={classes.accordion}
          title={intl.formatMessage({
            defaultMessage: "Inventory Information",
            description: "informations about product stock, header"
          })}
          quickPeek={
            (data.exportInfo.warehouses.length > 0 ||
              selectedInventoryFields.length > 0) && (
              <div className={classes.quickPeekContainer}>
                {selectedInventoryFields.slice(0, maxChips).map(field => (
                  <Chip
                    className={classes.chip}
                    label={getFieldLabel(field)}
                    onClose={() =>
                      onChange({
                        target: {
                          name: field,
                          value: false
                        }
                      })
                    }
                  />
                ))}
                {data.exportInfo.warehouses
                  .slice(0, maxChips - selectedInventoryFields.length)
                  .map(warehouseId => (
                    <Chip
                      className={classes.chip}
                      label={
                        warehouses.find(
                          warehouse => warehouse.value === warehouseId
                        ).label
                      }
                      onClose={() =>
                        onWarehouseSelect({
                          target: {
                            name: warehouseNamePrefix + warehouseId,
                            value: undefined
                          }
                        })
                      }
                    />
                  ))}
                {data.exportInfo.warehouses.length +
                  selectedInventoryFields.length >
                  maxChips && (
                  <Typography className={classes.moreLabel} variant="caption">
                    <FormattedMessage
                      defaultMessage="and {number} more"
                      description="there are more elements of list that are hidden"
                      values={{
                        number:
                          data.exportInfo.warehouses.length +
                          selectedInventoryFields.length -
                          maxChips
                      }}
                    />
                  </Typography>
                )}
              </div>
            )
          }
          data-test="inventory"
        >
          <div>
            <Option
              checked={selectedAllInventoryFields}
              name="all"
              onChange={() =>
                handleToggleAllFields(
                  inventoryFields,
                  !selectedAllInventoryFields
                )
              }
            >
              <FormattedMessage
                defaultMessage="Select All"
                description="selectt all options"
              />
            </Option>
            {inventoryFields.map(field => (
              <Option
                checked={data.exportInfo.fields.includes(field)}
                name={field}
                onChange={handleFieldChange}
                key={field}
              >
                {getFieldLabel(field)}
              </Option>
            ))}
          </div>
          {!productsUser.isStoreAdmin && (
            <>
              <Hr className={classes.hrWarehouses} />
              <Typography>
                <FormattedMessage defaultMessage="Export Product Stock Quantity to CSV" />
              </Typography>
              <div>
                <Option
                  checked={warehouses.every(warehouse =>
                    data.exportInfo.warehouses.includes(warehouse.value)
                  )}
                  name="all-warehouses"
                  onChange={onSelectAllWarehouses}
                >
                  <FormattedMessage
                    defaultMessage="Export stock for all warehouses"
                    description="option"
                  />
                </Option>
              </div>
              <Hr className={classes.hrWarehouses} />
              <Typography
                className={classes.warehousesLabel}
                variant="subtitle1"
              >
                <FormattedMessage
                  defaultMessage="Warehouses A to Z"
                  description="list of warehouses"
                />
              </Typography>
              {warehouses.map(warehouse => (
                <Option
                  checked={data.exportInfo.warehouses.includes(warehouse.value)}
                  name={warehouseNamePrefix + warehouse.value}
                  onChange={onWarehouseSelect}
                  key={warehouse.value}
                >
                  {warehouse.label}
                </Option>
              ))}
            </>
          )}
        </Accordion>
      )}
      {!productsUser.isSellerMaster && !productsUser.isStoreAdmin ? (
        <FieldAccordion
          idAccordion="seoInformation"
          title={intl.formatMessage({
            defaultMessage: "SEO Information",
            description: "informations about product seo, header"
          })}
          data={data}
          fields={[
            ProductFieldEnum.DESCRIPTION,
            ProductFieldEnum.NAME,
            ProductFieldEnum.PRODUCT_IMAGES,
            ProductFieldEnum.VARIANT_IMAGES
          ]}
          onChange={handleFieldChange}
          onToggleAll={handleToggleAllFields}
          data-test="seo"
        />
      ) : (
        <Accordion
          className={classes.accordion}
          idAccordion="seoInformation"
          title={intl.formatMessage({
            defaultMessage: "SEO Information",
            description: "informations about product, header"
          })}
          quickPeek={
            data.exportInfo.template &&
            data.exportInfo.template ===
              ProductTemplateEnum.SEO_INFORMATION_IMAGES && (
              <Chip
                className={classes.chip}
                label={data.exportInfo.template.replace(/_/g, " ")}
                key={data.exportInfo.template}
                onClose={() =>
                  onChange({
                    target: {
                      name: "exportInfo",
                      value: {
                        ...data.exportInfo,
                        template: null
                      }
                    }
                  })
                }
              />
            )
          }
          data-test="productInformation"
        >
          <RadioGroup aria-label="template" onChange={handleTemplateChange}>
            {[ProductTemplateEnum.SEO_INFORMATION_IMAGES].map((tpl, index) => (
              <FormControlLabel
                name={data.exportInfo.template}
                key={index}
                value={tpl}
                id={`seoInformationRadioRow${index}`}
                control={<Radio checked={tpl === data.exportInfo.template} />}
                label={
                  tpl
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace("seo information images", "Images")
                  // tpl
                  //   .toLowerCase()
                  //   .charAt(0)
                  //   .toUpperCase()
                  //    +
                  // tpl
                  // .slice(1)
                  // .replace(/_/g, " ")
                  // .toLowerCase()
                  // .replace("Seo information images", "Images")
                  // .replace("images", "Images")
                }
              />
            ))}
          </RadioGroup>
        </Accordion>
      )}
    </>
  );
};

ProductExportDialogInfo.displayName = "ProductExportDialogInfo";
export default ProductExportDialogInfo;
